<template>
  <b-modal
    id="optix-publish-modal"
    ref="modal"
    title="Publish to Platform"
    centered
    size="lg"
    no-close-on-backdrop
    @hide="close"
    @shown="handleModalShown"
  >
    <!-- Publishing Form -->
    <b-card class="mb-3">
      <b-form>
        <b-row>
          <b-col cols="12">
            <b-form-group
              label="Platform"
              label-for="platform-select"
              label-class="mb-1"
            >
              <b-form-select
                id="platform-select"
                v-model="platformId"
                size="sm"
                @change="handlePlatformChange"
              >
                <b-form-select-option
                  value=""
                  disabled
                  selected
                >
                  Select Platform
                </b-form-select-option>
                <b-form-select-option
                  v-for="platform in platforms"
                  :key="platform.id"
                  :value="platform.id"
                >
                  {{ platform.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <div
        v-if="platformId"
      >
        <b-checkbox
          v-model="isOptimized"
        >
          Publish with optimized data
        </b-checkbox>
      </div>

      <div
        v-if="isBynderMetaAvailable"
        class="d-flex justify-content-end mb-3"
      >
        <b-button
          variant="outline-primary"
          size="sm"
          @click="configureMetadata"
        >
          Configure Metadata
        </b-button>
      </div>

      <AdditionalMetaproperties
        :is-regular-publish="false"
      />
    </b-card>

    <!-- Success Results Block -->
    <b-card
      v-if="(salsifyPublishResults && salsifyPublishResults.length > 0) || (bynderPublishResults && bynderPublishResults.length > 0)"
      class="mb-3 border-success"
    >
      <div class="d-flex align-items-center mb-3">
        <h6 class="mb-0 text-success">
          Successfully Published to Platform
        </h6>
      </div>

      <div
        v-if="isSalsify"
        class="published-files"
      >
        <div
          v-for="result in salsifyPublishResults"
          :key="result.file_id"
          class="published-file-item mb-2 p-2 bg-light rounded"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ result.file_name }}</strong>
              <div class="text-muted small">
                File ID: {{ result.file_id }}
              </div>
              <div
                v-if="result.media_id"
                class="text-muted small"
              >
                Asset ID: {{ result.media_id }}
              </div>
            </div>
            <div>
              <a
                v-if="result.url"
                :href="result.url"
                target="_blank"
                class="btn btn-sm btn-outline-primary"
              >
                <i class="fas fa-external-link-alt mr-1" />
                View on Salsify
              </a>
              <span
                v-else
                class="badge badge-success"
              >
                Published
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isBynder"
        class="published-files"
      >
        <div
          v-for="result in bynderPublishResults"
          :key="result.file_id"
          class="published-file-item mb-2 p-2 bg-light rounded"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ result.file_name }}</strong>
              <div
                v-if="result.media_id"
                class="text-muted small"
              >
                Asset ID: {{ result.media_id }}
              </div>
            </div>
            <div>
              <span class="badge badge-success">
                Published
              </span>
            </div>
          </div>
        </div>
      </div>
    </b-card>
    <!-- Error Results Block -->
    <b-card
      v-if="publishErrors && publishErrors.length > 0"
      class="mb-3 border-danger"
    >
      <div class="d-flex align-items-center mb-3">
        <h6 class="mb-0 text-danger">
          Publishing Errors
        </h6>
      </div>

      <div class="error-files">
        <div
          v-for="error in publishErrors"
          :key="error.file_id"
          class="error-file-item mb-2 p-2 bg-light rounded"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1">
              <strong>{{ error.file_name }}</strong>
              <div
                v-if="error.media_id"
                class="text-muted small"
              >
                Asset ID: {{ error.media_id }}
              </div>
              <div class="text-danger small mt-1">
                {{ error.error_message }}
              </div>
            </div>
            <div>
              <a
                v-if="error.url"
                :href="error.url"
                target="_blank"
                class="btn btn-sm btn-outline-primary"
              >
                <i class="fas fa-external-link-alt mr-1" />
                View on Salsify
              </a>
              <span
                v-else
                class="badge badge-danger"
              >
                Failed
              </span>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end">
        <b-button
          class="mr-2"
          variant="outline-secondary"
          @click="$refs.modal.hide()"
        >
          {{
            (salsifyPublishResults && salsifyPublishResults.length > 0) ||
              (bynderPublishResults && bynderPublishResults.length > 0)
              ? 'Close'
              : 'Cancel'
          }}
        </b-button>
        <b-button
          v-if="isSalsify"
          variant="primary"
          :disabled="isPublishing || !platformId"
          @click="handleSalsifyPublish"
        >
          <b-spinner
            v-if="isPublishing"
            small
            class="mr-1"
          />
          {{ isPublishing ? 'Publishing...' : actionButtonText('Salsify') }}
        </b-button>
        <!-- <b-button
          v-if="isBynder"
          variant="primary"
          :disabled="isPublishing || !platformId"
          @click="handleBynderPublish"
        >
          <b-spinner
            v-if="isPublishing"
            small
            class="mr-1"
          />
          {{ isPublishing ? 'Publishing...' : actionButtonText('Bynder') }}
        </b-button> -->
      </div>
    </template>
  </b-modal>
</template>

<script>
import AdditionalMetaproperties from '@frontend/components/bynder/AdditionalMetaproperties.vue';
import {
  isBynderMetaAvailable as checkMeta,
  getPublishPreferences
} from '@frontend/group/modules/publish-utils/publish-utils';
import {
  BYNDER_EVENTS,
  bynderEventBroker,
} from '@frontend/group/modules/bynder/event-broker';
import * as bynder from '@frontend/constants/bynder';
import { mapGetters } from 'vuex';

export default {
  name: "OptixPublishToPlatformModal",

  components: {
    AdditionalMetaproperties,
  },

  props: {
    report: {
      type: Object,
      default: null,
    },
    optimizedContent: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    isPublishing: false,
    platformId: '',
    platforms: [],
    salsifyPublishResults: [],
    publishErrors: [],
    isSalsify: false,
    isBynder: false,
    bynderPublishResults: [],
    isBynderMetaAvailable: false,
    selectedPlatform: null,
    isBynderMeta: false,
    bynderMeta: null,
    platformKey: null,
    isOptimized: false
  }),

  computed: {
    ...mapGetters('bynder', ['additionalPropertiesById']),

    aiAdditionalProperties() {
      return this.additionalPropertiesById('ai-render');
    },

    filePath() {
      return this.report?.images?.[0]?.src ?? null;
    },
  },

  mounted() {
    bynderEventBroker.on(BYNDER_EVENTS.HANDLE_META, async ({ meta }) => {
      this.isBynderMeta = true;
      this.bynderMeta = meta;
    });
  },

  methods: {
    handleModalShown() {
      this.getPlatforms();
    },

    async getPlatforms() {
      try {
        const platforms = [];

        // TODO: Add Bynder support
        /* const [bynderRes, salsifyRes] = await Promise.all([
          axios.post('/platforms/is-platform-available', { platform: 'bynder' }),
          axios.post('/platforms/is-platform-available', { platform: 'salsify' }),
        ]);

        if (bynderRes.data.available) {
          platforms.push('bynder');
        } */

        const salsifyRes = await axios.post('/platforms/is-platform-available', { platform: 'salsify' });

        if (salsifyRes.data.available) {
          platforms.push('salsify');
        }

        const { data } = await axios.post('/platforms/get-platforms', { platforms: platforms });

        this.platforms = data.platforms || [];
      } catch (error) {
        console.error('Error fetching platforms:', error);
        toastr.error('Failed to load platforms');
      }
    },

    async handlePlatformChange() {
      this.salsifyPublishResults = [];
      this.bynderPublishResults = [];
      this.publishErrors = [];

      this.selectedPlatform = this.platforms.find(platform => platform.id == this.platformId);

      if (this.selectedPlatform) {
        const platformName = this.selectedPlatform.platform;

        switch (platformName) {
          case 'bynder': {
            this.isBynder = true;
            this.isSalsify = false;

            this.isBynderMetaAvailable = await checkMeta();

            this.platformKey = this.selectedPlatform.is_prod ? bynder.PLATFORM_KEY_PRODUCTION : bynder.PLATFORM_KEY_TESTING;

            if (this.isBynderMetaAvailable) {
              const savedMetadata = await getPublishPreferences(bynder.BYNDER_META_VALUES + this.platformKey, true);

              if (savedMetadata) {
                this.bynderMeta = savedMetadata;
                this.isBynderMeta = true;
              }

              bynderEventBroker.fire(BYNDER_EVENTS.HANDLE_PUBLISH_ADD_PROPS, { platformId: this.platformId });
            }
            break;
          }
          case 'salsify': {
            this.isSalsify = true;
            this.isBynder = false;
            break;
          }
          default: {
            this.isBynder = false;
            this.isSalsify = false;
            break;
          }
        }
      }
    },

    configureMetadata() {
      bynderEventBroker.fire(BYNDER_EVENTS.PUBLISH_INIT, {
        platformId: this.platformId,
        platformKey: this.platformKey,
        assetType: this.getFileExtension(),
      });
    },

    async handleSalsifyPublish() {
      if (!this.platformId) {
        toastr.error('Please select a platform.');
        return;
      }

      /* if (!this.filePath) {
        toastr.error('File path not provided.');
        return;
      } */

      this.isPublishing = true;

      this.salsifyPublishResults = [];
      this.bynderPublishResults = [];
      this.publishErrors = [];

      const path = new URL(this.filePath, window.location.origin).href;

      try {
        const { data } = await axios.post('/salsify/publish-assets', {
            platform_id: this.platformId,
            files: [{
                name: this.getFilenameFromUrl(path),
                url: path,
                original_path: path,
                asset_type: 'optix-image'
            }],
            optimized_content: this.isOptimized ? this.optimizedContent : null
        });

        if (data.status === 'completed' && Array.isArray(data.results)) {
          data.results.forEach(result => {
            if (result.status === 'success') {
              this.salsifyPublishResults.push({
                file_id: result.file_id,
                file_name: result.file_name,
                url: result.url || null
              });
            } else if (result.status === 'error') {
              this.publishErrors.push({
                file_id: result.file_id,
                file_name: result.file_name,
                error_message: result.error || 'Publishing failed',
                url: result.url || null
              });
            }
          });

          const successCount = this.salsifyPublishResults.length;
          const errorCount = this.publishErrors.length;

          if (successCount > 0 && errorCount === 0) {
            toastr.success(`${successCount} file(s) published successfully!`);
          } else if (successCount > 0 && errorCount > 0) {
            toastr.warning(`${successCount} file(s) published successfully, ${errorCount} failed. Check details below.`);
          } else if (errorCount > 0) {
            toastr.error(`Failed to publish ${errorCount} file(s). Check details below.`);
          }
        } else if (data.status === 'error') {
          toastr.error(data.message);
        }
      } catch (error) {
        console.error(`Error publishing: `, error);
        toastr.error(`Failed to publish assets`);
      } finally {
        this.isPublishing = false;
      }
    },

    // async handleBynderPublish() {
    //   if (!this.platformId) {
    //     toastr.error('Please select a platform.');
    //     return;
    //   }

    //   if (!this.filePath) {
    //     toastr.error('File path not provided.');
    //     return;
    //   }

    //   this.salsifyPublishResults = [];
    //   this.bynderPublishResults = [];
    //   this.publishErrors = [];

    //   this.isPublishing = true;
    //   const filename = this.getFilenameFromUrl(this.filePath);
    //   const path = new URL(this.filePath, window.location.origin).href;
    //     try {
    //         try {
    //             const { data } = await axios.post('/bynder/publish-asset', {
    //             publish_in_background: false,
    //             platform_id: this.platformId,
    //             file: {
    //                 name: filename,
    //                 url: path,
    //                 original_path: path,
    //                 extension: this.getFileExtension(),
    //                 asset_type: 'ai-generated'
    //             },
    //             is_bynder_meta: this.isBynderMeta,
    //             bynder_meta: this.bynderMeta,
    //             additional_properties: Object.keys(this.aiAdditionalProperties).length ? this.aiAdditionalProperties : null
    //             });

    //             if (data.status === 'success') {
    //             this.bynderPublishResults.push({
    //                 file_name: filename,
    //                 media_id: data.media_id,
    //             });
    //             } else {
    //             this.publishErrors.push({
    //                 file_name: filename,
    //                 error_message: data.message || 'Publishing failed'
    //             });
    //             }
    //         } catch (error) {
    //             console.error(`Error publishing:`, error);
    //             toastr.error(`Failed to publish assets`);
    //         }
    //     } catch (error) {
    //         console.error(`Error publishing:`, error);
    //         toastr.error(`Failed to publish assets`);
    //     } finally {
    //         this.isPublishing = false;
    //     }
    // },

    actionButtonText(platform) {
      return `Publish to ${platform}`;
    },

    getFileExtension(path) {
      let ext = path.split(".").pop().toLowerCase();

      if (!ext || ext === this.filePath) {
        ext = "jpg";
      }

      return ext;
    },

    getFilenameFromUrl(url, fallback = "untitled") {
         try {
            const parsed = new URL(url, window.location.origin);

            const fileParam = parsed.searchParams.get("file");

            let filename;

            if (fileParam) {
            const decoded = decodeURIComponent(fileParam);
            filename = decoded.substring(decoded.lastIndexOf("/") + 1);
            } else {
            const pathname = parsed.pathname || "";
            filename = pathname.substring(pathname.lastIndexOf("/") + 1);
            }

            if (!filename) return fallback;

            const lastDot = filename.lastIndexOf(".");
            if (lastDot > 0) {
            return filename.substring(0, lastDot);
            }

            return filename;

        } catch {
            return fallback;
        }
    },

    resetModal() {
      this.isPublishing = false;
      this.platformId = '';
      this.platforms = [];
      this.salsifyPublishResults = [];
      this.bynderPublishResults = [];
      this.publishErrors = [];
    },

    close() {
      this.resetModal();
      this.$bvModal.hide('optix-publish-modal');
    }
  }
}
</script>

<style scoped>
.published-file-item {
  border-left: 3px solid #28a745;
}

.published-files {
  max-height: 300px;
  overflow-y: auto;
}

.error-file-item {
  border-left: 3px solid #dc3545;
}

.error-files {
  max-height: 300px;
  overflow-y: auto;
}
</style>
